import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { ReikiBackground } from './components/ReikiBackground';
import { HeroReikiIcons } from './components/HeroReikiIcons';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [count, to, isInView]);
  return <motion.span ref={ref} data-id="element-0">{rounded}</motion.span>;
}
const testimonials = [{
  name: 'Estela (48)',
  loc: 'Buenos Aires',
  badge: 'Maestra Iniciadora',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'Al principio me dio cosa comprarlo tan barato, pensé que era puro humo. Pero el mapa de bloqueos es una locura total, posta. Me cambió la forma de atender.'
}, {
  name: 'Javi (32)',
  loc: 'Córdoba Capital',
  badge: 'Terapeuta Nivel III',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'Che, el kit está mortal. Las fichas de anamnesis en Canva me ahorraron un laburo bárbaro. Ahora mis sesiones tienen otra facha.'
}, {
  name: 'Mariela (55)',
  loc: 'Rosario',
  badge: 'Maestra Iniciadora',
  image: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'Soy reikiista hace mil años y nunca había visto algo tan bien organizado. El diario de 21 días me sirvió bocha para mis alumnos.'
}, {
  name: 'Sol (29)',
  loc: 'Mendoza',
  badge: 'Terapeuta Nivel III',
  image: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'Lo compré por las meditaciones y me terminé enviciando con los protocolos de ansiedad. Apliqué uno ayer y se fue re chocha.'
}, {
  name: 'Ricardo (42)',
  loc: 'Salta',
  badge: 'Terapeuta Nivel III',
  image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=400&h=400',
  text: 'Excelente el material. El tema de la protección energética me salvó la vida porque terminaba re quemado después de cada sesión.'
}];
const heroImages = ['https://cdn.magicpatterns.com/uploads/7hAbtPEv66tS7cRaYNE4r9/me_gusta_mucho_202604131502.png', 'https://cdn.magicpatterns.com/uploads/pYiKeNHymWKoebcvZXR1Ds/Diseno_sin_titulo_(1).png'];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);
  const [spots, setSpots] = useState(18);
  const [joinedCount, setJoinedCount] = useState(3265);
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '2048912638989861';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setJoinedCount(prev => prev + 1);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex(prev => (prev + 1) % heroImages.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-violet-50 text-violet-950 font-sans selection:bg-violet-200 selection:text-violet-950" data-id="element-1">
      <ReikiBackground data-id="element-2" />
      <UrgencyBanner spots={spots} onSale={handleSale} data-id="element-3" />

      {/* Hero Section */}
      <header className="relative pt-6 pb-16 overflow-hidden bg-gradient-to-b from-[#3b1059] via-[#6d2898] to-[#9b5de5] text-white" data-id="element-4">
        <HeroReikiIcons data-id="element-5" />
        {/* Magical Background Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.2)_0%,_transparent_60%)] pointer-events-none" data-id="element-6" />
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-pink-500/20 rounded-full blur-[100px] pointer-events-none" data-id="element-7" />
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-purple-400/20 rounded-full blur-[100px] pointer-events-none" data-id="element-8" />

        <div className="max-container px-4 relative z-10" data-id="element-9">
          {/* Top Trust Bar */}
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-xs md:text-sm font-medium mb-8 text-white/90 border-b border-white/10 pb-4" data-id="element-10">
            <div className="flex items-center gap-1.5" data-id="element-11">
              <Star size={14} className="fill-white" data-id="element-12" />
              <span data-id="element-13">
                +<AnimatedCounter from={0} to={3000} data-id="element-14" /> alumnos
              </span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-15">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-17" />
              </svg>
              <span data-id="element-18">Acceso inmediato</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-19">
              <ShieldCheck size={14} data-id="element-20" />
              <span data-id="element-21">Garantía 7 días</span>
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center" data-id="element-22">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-23">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black italic mb-6 leading-[1.1] drop-shadow-lg font-[Poppins] text-[#2e1065]" data-id="element-24">
                <span className="text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] text-[32px]" data-id="element-25">
                  Activá tu poder de
                </span>
                <br data-id="element-26" />
                <span className="text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" data-id="element-27">
                  sanación y transformá
                </span>
                <br data-id="element-28" />
                <span className="text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" data-id="element-29">
                  vidas desde el{' '}
                </span>
                <span className="text-[#f59e0b] drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]" data-id="element-30">
                  primer día
                </span>
              </h1>

              <p className="text-lg md:text-xl mb-8 font-medium text-white/90 drop-shadow-md" data-id="element-31">
                Convertite en terapeuta{' '}
                <span className="font-bold text-white" data-id="element-32">Reiki</span> con
                confianza,
                <br className="hidden md:block" data-id="element-33" />
                claridad y{' '}
                <span className="font-bold text-white" data-id="element-34">resultados reales</span>
              </p>
            </motion.div>

            <div className="relative w-full max-w-lg mx-auto mb-10 h-[300px] md:h-[450px] flex items-center justify-center overflow-hidden" data-id="element-35">
              <AnimatePresence mode="wait" data-id="element-36">
                <motion.img key={heroIndex} src={heroImages[heroIndex]} alt="Guía Reiki Terapéutico" initial={{
                opacity: 0,
                x: 100
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -100
              }} transition={{
                duration: 0.8,
                ease: 'easeInOut'
              }} className="w-full h-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] absolute" data-id="element-37" />
              </AnimatePresence>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="flex flex-col gap-4 max-w-sm mx-auto" data-id="element-38">
              <button onClick={scrollToPurchase} className="w-full bg-gradient-to-b from-[#fbbf24] to-[#d97706] text-white font-bold py-4 px-6 rounded-full text-lg shadow-[0_8px_20px_rgba(217,119,6,0.5)] border border-[#fcd34d] hover:scale-[1.02] transition-transform" data-id="element-39">
                QUIERO APRENDER REIKI AHORA
              </button>

              <button onClick={scrollToPurchase} className="w-full bg-gradient-to-b from-[#8b5cf6] to-[#5b21b6] text-white font-bold py-4 px-6 rounded-full text-lg shadow-[0_8px_20px_rgba(91,33,182,0.5)] border border-[#a78bfa] hover:scale-[1.02] transition-transform" data-id="element-40">
                ACCEDER A LA GUÍA COMPLETA
              </button>

              <p className="text-white/80 mt-4 text-[12px]" data-id="element-41">
                Súmate a miles de personas transformando sus vidas
                <br data-id="element-42" />
                con el poder del{' '}
                <span className="font-bold text-white" data-id="element-43">Reiki.</span>
              </p>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Sales Copy */}
      <section className="py-24 bg-white border-y border-violet-100 pt-[5px] pb-[5px] space-y-[0px]" data-id="element-44">
        <div className="max-w-4xl mx-auto px-4" data-id="element-45">
          <div className="mb-20" data-id="element-46">
            <p className="text-3xl md:text-4xl font-bold text-violet-950 mb-8 leading-tight" data-id="element-47">
              El mundo está lleno de terapeutas con buenas intenciones, pero muy
              pocos tienen una metodología.
            </p>
            <div className="space-y-6 text-violet-800/70 text-lg leading-relaxed" data-id="element-48">
              <p data-id="element-49">
                La mayoría de los practicantes de Reiki operan desde la
                intuición pura. Y aunque la intuición es poderosa, sin una
                estructura profesional tiene un techo muy bajo.
              </p>
              <p className="border-l-4 border-violet-600 pl-6 italic text-violet-900" data-id="element-50">
                Terminas las sesiones agotada, con dudas sobre si realmente
                tocaste la raíz del problema y, lo que es peor, con clientes que
                no perciben un cambio tangible y no regresan.
              </p>
              <p data-id="element-51">
                Para ser respetada como profesional y ver transformaciones
                reales, necesitas más que intención. Necesitas un Sistema de
                Maestría.
              </p>
            </div>
          </div>

          <div className="bg-violet-50 p-8 md:p-12 rounded-[2rem] border border-violet-200 relative overflow-hidden" data-id="element-52">
            <div className="absolute top-0 right-0 p-8 opacity-10" data-id="element-53">
              <ShieldCheck size={120} className="text-violet-600" data-id="element-54" />
            </div>
            
            <h3 className="text-violet-950 mb-6 uppercase italic font-[900] font-[Lato,_sans-serif] text-[36px]" data-id="element-55">
              KIT COMPLETO DE SANACIÓN REIKI
            </h3>
            <p className="text-xl text-violet-800 mb-8 leading-relaxed" data-id="element-56">
              La infraestructura definitiva para el terapeuta holístico que
              exige excelencia. Este no es un curso más. Es tu arsenal
              operativo.
            </p>
            <ul className="grid md:grid-cols-2 gap-4" data-id="element-57">
              {['Protocolos de diagnóstico avanzado', 'Fichas de anamnesis clínica', 'Guías de protección energética', 'Metodología de resultados tangibles'].map((item, i) => <li key={i} className="flex items-center gap-3 text-violet-900 font-medium" data-id="element-58">
                  <CheckCircle2 className="text-violet-600 shrink-0" size={20} data-id="element-59" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <WhatYouReceive data-id="element-60" />

      {/* Bonuses */}
      <section className="py-24 bg-violet-50 pt-[20px] pb-[20px]" data-id="element-61">
        <div className="max-container px-4" data-id="element-62">
          <div className="text-center mb-16" data-id="element-63">
            <h2 className="text-4xl md:text-6xl font-black text-violet-950 uppercase italic mb-4 font-[Lato,_sans-serif]" data-id="element-64">
              Bonos Exclusivos
            </h2>
            <p className="text-violet-600 font-bold tracking-widest uppercase" data-id="element-65">
              Gratis solo por hoy
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-66">
            <BonusCard number="01" title="Secretos Ancestrales de los 7 Chakras" image="https://cdn.magicpatterns.com/uploads/uMgyUyUPnGQJyaWFBVn3Vw/Haceme_libro_con_202604081850.jpg" description="Identifica la raíz emocional de cada síntoma físico en segundos." value="$47 USD" data-id="element-67" />
            <BonusCard number="02" title="Mapa de Bloqueos Emocionales y síntomas Físicos" image="https://cdn.magicpatterns.com/uploads/eQ7YVMBJDTVz6QqtMmLpkG/hacer_imagen_estilo_202604081854.jpg" description="Guiones listos para profundizar la sanación entre sesiones." value="$37 USD" data-id="element-68" />
            <BonusCard number="03" title="Biblioteca Secreta de las 50 Meditaciones de Poder" image="https://cdn.magicpatterns.com/uploads/aHbwXvGeGeLDCjHD3NmknJ/hacer_imagen_estilo_202604081856.jpg" description="Aprende a no absorber la densidad energética de tus pacientes." value="$27 USD" data-id="element-69" />
            <BonusCard number="04" title="Jornada Energética para Activar tu Potencial Divino" image="https://cdn.magicpatterns.com/uploads/ibkw9fw15obqjcufgV8F17/necesito_hacer_esta_202604131807.png" description="Métodos simples y eficaces de protección y limpieza energética para mantener tu vibración siempre elevada." value="$37 USD" data-id="element-70" />
            <BonusCard number="05" title="PROTOCOLO MAGNÉTICO DE MANIFESTACIÓN" image="https://cdn.magicpatterns.com/uploads/f6i2T5L2vzqUXnjckrx5Kn/hacer_imagen_estilo_202604081859.jpg" description="Herramienta poderosa para alinear tus deseos, elevar tu frecuencia y manifestar con claridad y propósito." value="$27 USD" data-id="element-71" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-b from-violet-50 to-pink-50 pt-[5px] pb-[5px]" data-id="element-72">
        {/* Soft blur background elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-pink-100/80 via-transparent to-transparent pointer-events-none" data-id="element-73" />
        <div className="absolute -left-40 top-40 w-96 h-96 bg-pink-200/20 rounded-full blur-3xl pointer-events-none" data-id="element-74" />
        <div className="absolute -right-40 bottom-20 w-96 h-96 bg-pink-100 rounded-full blur-3xl shadow-[0_0_100px_rgba(219,39,119,0.1)] pointer-events-none" data-id="element-75" />

        <div className="max-container px-4 relative z-10" data-id="element-76">
          <div className="text-center mb-16" data-id="element-77">
            
            <h3 className="text-4xl md:text-5xl font-black text-violet-950 uppercase tracking-tighter italic font-[Inter,_sans-serif]" data-id="element-78">
              Lo que dicen los Maestros
            </h3>
          </div>

          <div className="max-w-4xl mx-auto relative" data-id="element-79">
            <div className="overflow-hidden" data-id="element-80">
              <motion.div key={currentTestimonial} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} onDragEnd={(_, info) => {
              if (info.offset.x > 100) {
                prevTestimonial();
              } else if (info.offset.x < -100) {
                nextTestimonial();
              }
            }} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} className="bg-white p-8 md:p-12 rounded-[3rem] shadow-[0_30px_60px_-12px_rgba(219,39,119,0.15)] border border-pink-100 relative backdrop-blur-sm cursor-grab active:cursor-grabbing" data-id="element-81">
                <div className="flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left" data-id="element-82">
                  <div className="shrink-0 relative" data-id="element-83">
                    <div className="absolute -inset-2 bg-gradient-to-br from-pink-400 to-violet-400 rounded-full blur opacity-30 animate-pulse" data-id="element-84" />
                    <img src={testimonials[currentTestimonial].image} alt={testimonials[currentTestimonial].name} className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-xl relative z-10" data-id="element-85" />
                  </div>

                  <div className="flex-1" data-id="element-86">
                    <div className="flex justify-center md:justify-start text-pink-500 mb-6 gap-1" data-id="element-87">
                      {[...Array(5)].map((_, idx) => <Star key={idx} size={20} fill="currentColor" strokeWidth={1} data-id="element-88" />)}
                    </div>

                    <p className="text-violet-950 text-xl md:text-2xl leading-relaxed font-light italic mb-8" data-id="element-89">
                      "{testimonials[currentTestimonial].text}"
                    </p>

                    <div className="pt-6 border-t border-pink-50" data-id="element-90">
                      <span className="inline-block bg-pink-100 text-pink-700 text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-2" data-id="element-91">
                        {testimonials[currentTestimonial].badge}
                      </span>
                      <h4 className="text-violet-950 font-black text-xl" data-id="element-92">
                        {testimonials[currentTestimonial].name}
                      </h4>
                      <p className="text-pink-400 font-bold uppercase tracking-widest text-sm" data-id="element-93">
                        {testimonials[currentTestimonial].loc}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white p-4 rounded-full shadow-xl text-pink-600 hover:bg-pink-50 transition-colors z-20 hidden md:block" data-id="element-94">
              <ArrowRight className="rotate-180" size={24} data-id="element-95" />
            </button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white p-4 rounded-full shadow-xl text-pink-600 hover:bg-pink-50 transition-colors z-20 hidden md:block" data-id="element-96">
              <ArrowRight size={24} data-id="element-97" />
            </button>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-3 mt-12" data-id="element-98">
              {testimonials.map((_, i) => <button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-3 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-12 bg-pink-500' : 'w-3 bg-pink-200 hover:bg-pink-300'}`} data-id="element-99" />)}
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="purchase" className="py-24 bg-violet-100 relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-100">
        <div className="max-w-4xl mx-auto px-4 relative z-10" data-id="element-101">
          <div className="bg-violet-950 text-white p-4 text-center font-black uppercase tracking-widest mb-8 animate-pulse" data-id="element-102">
            OFERTA VÁLIDA SOLO POR HOY: {currentDate}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-103">
            {['Descargue todos los archivos, guárdelos en una carpeta segura y téngalos para siempre.', 'Pago único y descarga inmediata.', 'Materiales listos para impresión.', 'Herramientas desarrolladas por terapeutas especialistas.'].map((text, i) => <div key={i} className="flex items-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-violet-200 shadow-sm" data-id="element-104">
                <CheckCircle2 className="text-violet-600 shrink-0" size={20} data-id="element-105" />
                <p className="text-violet-900 text-sm font-bold leading-tight" data-id="element-106">
                  {text}
                </p>
              </div>)}
          </div>

          <div className="mb-12" data-id="element-107">
            <img src="https://cdn.magicpatterns.com/uploads/nyPwci9WYZJ1rzj9zkwu5e/Diseno_sin_titulo_(1).png" alt="Paquete Completo Reiki Terapéutico" className="w-full max-w-5xl mx-auto h-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.2)]" data-id="element-108" />
          </div>

          <div className="border-4 border-violet-950 p-8 md:p-12 rounded-3xl bg-white shadow-xl" data-id="element-109">
            <div className="mb-10" data-id="element-110">
              <div className="space-y-4 bg-violet-50 p-6 rounded-2xl border border-violet-100" data-id="element-111">
                <h4 className="text-violet-950 font-black uppercase tracking-widest text-sm mb-2" data-id="element-112">
                  LO QUE TE LLEVAS HOY:
                </h4>
                <ul className="space-y-3" data-id="element-113">
                  {['Guía Reiki Terapéutico (Manual Completo)', 'Bono 1: Secretos Ancestrales de los 7 Chakras', 'Bono 2: Mapa de Bloqueos Emocionales y Síntomas Físicos', 'Bono 3: Biblioteca Secreta de las 50 Meditaciones de Poder', 'Bono 4: Jornada Energética para Activar tu Potencial Divino', 'Bono 5: Protocolo Magnético de Manifestación'].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-violet-900 font-medium text-sm" data-id="element-114">
                      <CheckCircle2 className="text-green-600 shrink-0" size={18} data-id="element-115" />
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-8 mb-12 text-center" data-id="element-116">
              <div className="flex flex-col items-center" data-id="element-117">
                <span className="text-green-600 flex items-center gap-2 font-black text-xl" data-id="element-118">
                  <Users size={24} data-id="element-119" /> 48
                </span>
                <span className="text-violet-400 text-xs uppercase font-bold" data-id="element-120">
                  Mirando ahora
                </span>
              </div>
              <div className="flex flex-col items-center" data-id="element-121">
                <span className="text-red-600 flex items-center gap-2 font-black text-xl" data-id="element-122">
                  <Zap size={24} data-id="element-123" /> {spots}
                </span>
                <span className="text-violet-400 text-xs uppercase font-bold" data-id="element-124">
                  Cupos Libres
                </span>
              </div>
            </div>

            <div className="bg-violet-50 p-8 rounded-2xl text-center mb-10 border border-violet-100" data-id="element-125">
              <div className="text-violet-950 font-black text-4xl mb-2" data-id="element-126">
                <AnimatedCounter from={0} to={joinedCount} data-id="element-127" />
              </div>
              <div className="text-violet-400 text-sm uppercase font-bold tracking-widest" data-id="element-128">
                PERSONAS YA ACCEDIERON
              </div>
            </div>

            <div className="text-center mb-12" data-id="element-129">
              <p className="text-violet-300 line-through text-2xl mb-2" data-id="element-130">
                Antes $59.900 ARS
              </p>
              <p className="md:text-8xl tracking-tighter font-[800] text-[#319E0D] text-[45px]" data-id="element-131">
                $19.990 ARS
              </p>
            </div>

            <div className="flex justify-center mb-6" data-id="element-132">
              <div className="relative group overflow-hidden px-6 py-3 bg-violet-50 rounded-xl border border-violet-100" data-id="element-133">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-200/40 to-transparent -translate-x-full animate-shimmer" data-id="element-134" />
                <p className="text-violet-950 font-bold flex items-center gap-2" data-id="element-135">
                  💳 Hasta 3 cuotas sin interés
                </p>
              </div>
            </div>

            <a href="https://codex-labs.shop/cart/43426863579195:1" target="_blank" rel="noopener noreferrer" className="w-full text-white font-black py-6 rounded-2xl uppercase tracking-tighter hover:bg-violet-800 hover:scale-[1.02] transition-all duration-300 shadow-lg mb-8 flex items-center justify-center gap-4 bg-[#319E0D] text-[28px]" data-id="element-136">
              SÍ, QUIERO LA OFERTA AHORA
              <ArrowRight size={32} data-id="element-137" />
            </a>

            <div className="space-y-8" data-id="element-138">
              <div className="text-center" data-id="element-139">
                <p className="text-violet-400 text-xs uppercase font-black tracking-widest mb-4" data-id="element-140">
                  Vendedor Destacado
                </p>
                <img src="https://cdn.magicpatterns.com/uploads/skccFFLzCHBogMzzjkfEmF/7ef4071d-c511-4325-9d9d-f2158f7b8632.jpg" alt="MercadoLíder Platinum" className="mx-auto rounded-xl max-w-[300px] w-full shadow-sm" data-id="element-141" />
              </div>

              <div className="text-center" data-id="element-142">
                <p className="text-violet-400 text-xs uppercase font-black tracking-widest mb-4" data-id="element-143">
                  Pagos Procesados de Forma Segura
                </p>
                <img src="https://cdn.magicpatterns.com/uploads/eU118ieSDgWuz64T6oQtAx/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="mx-auto max-w-[400px] w-full" data-id="element-144" />
              </div>

              <div className="pt-6 border-t border-violet-50" data-id="element-145">
                <p className="text-violet-950 font-black text-sm uppercase tracking-widest mb-6 text-center" data-id="element-146">
                  Tu Compra está Protegida
                </p>
                <div className="flex justify-center items-center gap-8" data-id="element-147">
                  <img src="https://cdn.magicpatterns.com/uploads/7eCtKk7WA3cLyGaeie1cdM/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-16 w-auto object-contain" data-id="element-148" />
                  <img src="https://cdn.magicpatterns.com/uploads/fpbSfdYZaTGuGJTHBKUizP/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-20 w-auto object-contain" data-id="element-149" />
                </div>
              </div>

              <div className="flex flex-col items-center gap-4" data-id="element-150">
                <p className="text-violet-400 text-xs flex items-center gap-2" data-id="element-151">
                  <ShieldCheck size={14} data-id="element-152" /> Pago 100% Seguro y Encriptado
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Reviews data-id="element-153" />

      <FAQ data-id="element-154" />

      <footer className="py-12 border-t border-violet-100 text-center text-violet-600 text-sm" data-id="element-155">
        <p data-id="element-156">© 2025 Guía Reiki Terapéutico. Todos los derechos reservados.</p>
      </footer>
    </div>;
}